import { router } from "@inertiajs/react"
import { Loader2, PenLine } from "lucide-react"
import { useState } from "react"

import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"

import { formatDate } from "./status-chip"
import type { WorkOrderRow } from "./types"

/**
 * THE HEADLINE. Marathon's ask, verbatim: "Who are we waiting on? What are we
 * waiting on? How long have we been waiting? When's the last time we followed
 * up?" — this banner is all four in one line, at the top of the page, before
 * anything else the operator has to read.
 *
 * "Draft a chaser" is here rather than buried in the correspondence thread
 * because the answer to "we last followed up 9 days ago" should be one click,
 * not a scroll and a blank compose box.
 */
export function WaitingBanner({
  workOrder,
  counterpartyName,
  correspondencesPath,
  canEdit,
}: {
  workOrder: WorkOrderRow
  counterpartyName: string | null
  correspondencesPath: string
  canEdit: boolean
}) {
  const [drafting, setDrafting] = useState(false)

  // A closed work order is not waiting on anyone. Saying "waiting on us" for a
  // car that went back in service last week is the kind of wrong that makes
  // people stop reading the banner entirely.
  if (!workOrder.open || !workOrder.waiting_on_label) {
    return (
      <div className="rounded-lg border border-emerald-300 bg-emerald-50 px-4 py-3 dark:border-emerald-800/60 dark:bg-emerald-950/40">
        <p className="text-sm font-medium text-emerald-900 dark:text-emerald-200">
          Closed — {workOrder.status_label.toLowerCase()}
          {workOrder.car_mark_number ? ` · ${workOrder.car_mark_number}` : ""}
        </p>
      </div>
    )
  }

  const days = workOrder.days_waiting
  const severe = (days ?? 0) >= 30
  const who = counterpartyName
    ? `${workOrder.waiting_on_label} (${counterpartyName})`
    : workOrder.waiting_on_label

  function draftChaser() {
    if (drafting) return
    setDrafting(true)
    router.post(
      correspondencesPath,
      { draft_chaser: 1 },
      { preserveScroll: true, onFinish: () => setDrafting(false) },
    )
  }

  return (
    <div
      className={cn(
        "flex flex-wrap items-center justify-between gap-3 rounded-lg border px-4 py-3",
        severe
          ? "border-red-300 bg-red-50 dark:border-red-800/60 dark:bg-red-950/40"
          : "border-amber-300 bg-amber-50 dark:border-amber-700/60 dark:bg-amber-950/40",
      )}
    >
      <div>
        <p
          className={cn(
            "text-base font-semibold",
            severe
              ? "text-red-900 dark:text-red-200"
              : "text-amber-900 dark:text-amber-200",
          )}
        >
          Waiting on {who}
          {days != null && ` — ${days} day${days === 1 ? "" : "s"}`}
        </p>
        <p
          className={cn(
            "mt-0.5 text-sm",
            severe
              ? "text-red-800 dark:text-red-300"
              : "text-amber-800 dark:text-amber-300",
          )}
        >
          {workOrder.last_followed_up_at
            ? `Last follow-up ${formatDate(workOrder.last_followed_up_at)}`
            : "No follow-up sent yet"}
          {workOrder.waiting_since &&
            ` · waiting since ${formatDate(workOrder.waiting_since)}`}
        </p>
      </div>

      {canEdit && (
        <Button variant="outline" size="sm" onClick={draftChaser}>
          {drafting ? (
            <Loader2 className="mr-1 size-4 animate-spin" />
          ) : (
            <PenLine className="mr-1 size-4" />
          )}
          Draft a chaser
        </Button>
      )}
    </div>
  )
}
